/**
 * KHO GIAO DIỆN — đăng ký, và áp một giao diện lên tài liệu.
 *
 * THÊM MỘT GIAO DIỆN: tạo `web/src/themes/<tên>.ts` theo mẫu `kim-long.ts`, thêm một
 * dòng `import` và một phần tử vào mảng `THEMES` ngay bên dưới. Hết. Không thành phần
 * nào phải sửa, không tệp CSS nào phải sửa, và giao diện mới lập tức xuất hiện ở trang
 * xem trước `/giao-dien`.
 *
 * ÁNH XẠ TÊN MIỀN NẰM Ở MÁY CHỦ, KHÔNG CÒN Ở ĐÂY. Trước đây tệp này giữ một mảng
 * `BRAND_SITES` viết cứng, nên gắn một thương hiệu mới là sửa mã và triển khai lại cả
 * nền tảng. Nay `GET /v1/brand` trả về thương hiệu ứng với tên miền đang gọi, đọc từ
 * bảng `brand` — thêm thương hiệu chỉ là thêm một hàng dữ liệu.
 */

import type { Theme } from './types';
import { kimLong } from './kim-long';
import { ngocBich } from './ngoc-bich';
import { neonSaiGon } from './neon-sai-gon';
import { bachKim } from './bach-kim';

export type { Theme, ShellLayout, NavStyle, MarkId, ColorScheme } from './types';

/** Kho giao diện. Thêm giao diện mới vào đây. */
export const THEMES: Theme[] = [kimLong, ngocBich, neonSaiGon, bachKim];

export const DEFAULT_THEME_ID = kimLong.id;

const STORAGE_KEY = 'gikidi.giao-dien';
const BRAND_CACHE_KEY = 'gikidi.thuong-hieu';
/** Tham số URL để xem trước một giao diện mà không cần tên miền riêng. */
export const THEME_QUERY_PARAM = 'giao-dien';

export function themeById(id: string | null | undefined): Theme | null {
  if (!id) return null;
  return THEMES.find((t) => t.id === id) ?? null;
}

/** Thương hiệu ứng với tên miền hiện tại, do máy chủ trả về. */
export interface Brand {
  id: string;
  name: string;
  theme_id: string;
  domain: string | null;
  /** Chỉ bật cho tên miền trình diễn của nền tảng. */
  allow_theme_preview: boolean;
}

function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Cửa sổ ẩn danh, hoặc người dùng chặn lưu trữ. Không phải lỗi — chỉ là không có gì đã lưu.
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Không lưu được thì thôi; lựa chọn chỉ sống hết phiên này.
  }
}

export function rememberTheme(id: string): void {
  writeLocal(STORAGE_KEY, id);
}

/**
 * Thương hiệu đã nhớ của ĐÚNG tên miền này.
 *
 * Máy chủ là nguồn sự thật, nhưng gọi máy chủ thì bất đồng bộ, mà giao diện phải đúng
 * ngay từ khung hình đầu — nếu không người mở website thấy một nháy nền tối trước khi
 * nền sáng hiện ra. Nên kết quả lần trước được nhớ lại theo tên miền và dùng ngay,
 * rồi câu trả lời thật của máy chủ ghi đè khi tới.
 *
 * Nhớ KÈM TÊN MIỀN là điều bắt buộc: nhớ trần thì mở website của nhà khai thác khác
 * sẽ thấy nhận diện của nhà khai thác trước trong khoảnh khắc đầu.
 */
function cachedBrand(host: string): Brand | null {
  const raw = readLocal(BRAND_CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { host?: string; brand?: Brand };
    return parsed.host === host && parsed.brand ? parsed.brand : null;
  } catch {
    return null;
  }
}

function cacheBrand(host: string, brand: Brand | null): void {
  writeLocal(BRAND_CACHE_KEY, JSON.stringify({ host, brand }));
}

/** Hỏi máy chủ thương hiệu của tên miền đang gọi. Lỗi mạng trả `null`, không ném. */
export async function fetchBrand(): Promise<Brand | null> {
  try {
    const res = await fetch('/v1/brand', { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const body = (await res.json()) as { brand: Brand | null };
    cacheBrand(window.location.hostname, body.brand);
    return body.brand;
  } catch {
    return null;
  }
}

export interface Resolution {
  theme: Theme;
  /** Vì sao giao diện này được chọn — hiển thị ở trang xem trước. */
  source: 'tham-so-url' | 'thuong-hieu' | 'da-luu' | 'mac-dinh';
  brand: Brand | null;
}

/**
 * Chọn giao diện, cho một thương hiệu đã biết (hoặc chưa biết).
 *
 * Thứ tự ưu tiên, và lý do của thứ tự đó:
 *
 *   1. `?giao-dien=` — ý muốn tường minh của người đang xem, luôn thắng.
 *   2. Thương hiệu của tên miền — thắng lựa chọn đã lưu. Đây là điểm dễ làm sai nhất:
 *      một người từng bấm xem trước ở trang trình diễn sẽ mang theo lựa chọn cũ trong
 *      `localStorage`; nếu lựa chọn đó thắng, người ấy vào website thật của một nhà
 *      khai thác lại thấy thương hiệu của nhà khai thác khác.
 *   3. Lựa chọn đã lưu — chỉ khi thương hiệu **cho phép** xem thử, tức tên miền trình
 *      diễn của nền tảng, nơi đổi qua lại chính là mục đích.
 *   4. Mặc định.
 */
export function resolveTheme(
  location: { hostname: string; search: string },
  brand: Brand | null,
): Resolution {
  const requested = themeById(new URLSearchParams(location.search).get(THEME_QUERY_PARAM));
  if (requested) return { theme: requested, source: 'tham-so-url', brand };

  const choPhepXemThu = brand?.allow_theme_preview ?? brand === null;
  if (brand && !choPhepXemThu) {
    const cua = themeById(brand.theme_id);
    if (cua) return { theme: cua, source: 'thuong-hieu', brand };
  }

  const stored = themeById(readLocal(STORAGE_KEY));
  if (stored && choPhepXemThu) return { theme: stored, source: 'da-luu', brand };

  const cua = themeById(brand?.theme_id);
  if (cua) return { theme: cua, source: 'thuong-hieu', brand };

  return { theme: themeById(DEFAULT_THEME_ID)!, source: 'mac-dinh', brand };
}

/** Giao diện đoán được NGAY, không chờ mạng: dùng thương hiệu đã nhớ của tên miền này. */
export function resolveFromCache(location: { hostname: string; search: string }): Resolution {
  return resolveTheme(location, cachedBrand(location.hostname));
}

/**
 * Áp giao diện lên tài liệu.
 *
 * Gọi được nhiều lần; mỗi lần ghi đè trọn bộ biến của lần trước vì mọi giao diện đều
 * khai đủ cùng một tập khóa.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;

  for (const [key, value] of Object.entries(theme.tokens)) {
    root.style.setProperty(`--${key}`, value);
  }

  // Các thuộc tính này là chỗ bám của những khác biệt CẤU TRÚC — thanh trên hay cột
  // trái, mục đang chọn vẽ kiểu gì. CSS đọc chúng qua bộ chọn thuộc tính trên `html`.
  root.dataset.scheme = theme.scheme;
  root.dataset.layout = theme.layout;
  root.dataset.nav = theme.navStyle;
  root.dataset.theme = theme.id;

  // Cho trình duyệt biết nền sáng hay tối, để ô nhập, thanh cuộn và bảng chọn ngày
  // của chính nó không lệch tông với trang.
  root.style.colorScheme = theme.scheme;

  ensureFonts(theme);

  const themeColor = theme.tokens['bg-root'];
  if (themeColor) {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = themeColor;
  }
}

/** Tiêu đề trình duyệt: tên thương hiệu thật nếu có, nếu không thì tên của giao diện. */
export function applyTitle(theme: Theme, brand: Brand | null): void {
  document.title = brand?.name ?? theme.brandName;
}

/**
 * Nạp bộ chữ của giao diện.
 *
 * Mỗi giao diện có thẻ `<link>` riêng, thêm một lần rồi giữ lại: đổi qua lại ở trang
 * xem trước sẽ không phải tải lại bộ chữ đã tải. Bộ chữ nào không tải được thì
 * `font-family` rơi xuống lớp dự phòng đã khai trong token — trang vẫn đọc được.
 */
function ensureFonts(theme: Theme): void {
  const id = `theme-font-${theme.id}`;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = theme.fonts.href;
  document.head.appendChild(link);
}
